/* eslint-disable @typescript-eslint/no-explicit-any -- forwarded notes field shape */
import React from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ExcerptCard, type ExcerptRow } from "./ExcerptCard";
import { extractSearchTokens } from "@/lib/highlight";
import { countTokenHits } from "./excerptsListHelpers";

interface Props {
  loading: boolean;
  items: ExcerptRow[];
  searchQuery: string;
  searchParams: URLSearchParams;
  hasFilters: boolean;
  selectMode: boolean;
  selected: Set<string>;
  onClearFilter: () => void;
  onToggleSelect: (id: string) => void;
  onDuplicate: (e: React.MouseEvent, it: ExcerptRow) => void;
  onSendToImageStudio: (e: React.MouseEvent, it: ExcerptRow) => void;
  onChipClick: (kind: "tag" | "topic", name: string) => void;
  scrollKey: string;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}


export const ExcerptsListGrid: React.FC<Props> = ({
  loading,
  items,
  searchQuery,
  searchParams,
  hasFilters,
  selectMode,
  selected,
  onClearFilter,
  onToggleSelect,
  onDuplicate,
  onSendToImageStudio,
  onChipClick,
  scrollKey,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}) => {

  if (loading) {
    return (
      <div className="grid gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="shadow-soft">
            <CardContent className="space-y-3 py-5">
              <Skeleton className="h-5 w-11/12" />
              <Skeleton className="h-5 w-9/12" />
              <Skeleton className="h-4 w-1/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Card className="shadow-soft">
        <CardContent className="space-y-4 py-16 text-center">
          <BookOpen className="mx-auto h-10 w-10 text-muted-foreground" />
          <h2 className="font-serif text-xl">
            {hasFilters ? "No matching excerpts" : "No excerpts yet"}
          </h2>
          <p className="text-muted-foreground">
            {hasFilters
              ? "No excerpts match this filter."
              : "Add your first quote to start building your library."}
          </p>
          {hasFilters ? (
            <Button variant="outline" onClick={onClearFilter}>Clear filter</Button>
          ) : (
            <Link to="/new-excerpt"><Button>Add an excerpt</Button></Link>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4">
      {items.map((it) => {
        const tokens = extractSearchTokens(searchQuery);
        const bodyHits = countTokenHits(it.body_text ?? "", tokens);
        const notesHits = countTokenHits((it as any).notes ?? "", tokens);
        const matchCount = bodyHits + notesHits;
        return (
          <ExcerptCard
            key={it.id}
            excerpt={it}
            selectMode={selectMode}
            isSelected={selected.has(it.id)}
            onToggleSelect={onToggleSelect}
            onDuplicate={onDuplicate}
            onSendToImageStudio={onSendToImageStudio}
            onChipClick={onChipClick}
            highlightTokens={tokens}
            matchCount={matchCount}
            notesMatchCount={notesHits}
            forwardParams={searchParams}
            onNavigateToDetail={() => {
              const container = document.getElementById("app-scroll-container");
              const y = container ? container.scrollTop : window.scrollY;
              sessionStorage.setItem(scrollKey, String(y));
            }}
          />
        );
      })}
      {hasMore && onLoadMore && (
        <div className="flex justify-center pt-2">
          <Button variant="outline" onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
};

